import { db } from '../../data/firebase/firebaseConfig.js';
import { doc, getDoc } from 'firebase/firestore';
import { ResumePreview } from '../preview/ResumePreview.js';
import { Resume } from '../../domain/models/Resume.js';
import { StatsCalculator } from '../../domain/services/StatsCalculator.js';
import { AnimatedStat } from './AnimatedStat.js';
import { Toast } from '../common/Toast.js';
import { writeTextToClipboard } from '../common/clipboard.js';

// ---------------------------------------------------------------------------
// Fun distance comparisons (pick the highest threshold the user has exceeded)
// ---------------------------------------------------------------------------

const DISTANCE_COMPARISONS = [
  { threshold: 42, text: () => `That's your first marathon worth of running 🏁` },
  { threshold: 84, text: () => `That's 2 marathons completed` },
  { threshold: 100, text: () => `You've run roughly Bengaluru to Mysuru` },
  { threshold: 150, text: () => `That's Mumbai to Pune on foot` },
  { threshold: 200, text: () => `You've crossed 5 marathons worth of distance` },
  { threshold: 300, text: () => `That's Pune to Mumbai and back halfway` },
  { threshold: 400, text: () => `That's like running Bengaluru to Chennai` },
  { threshold: 500, text: () => `That's Mumbai to Goa on foot` },
  { threshold: 600, text: () => `You've run farther than Delhi to Jaipur and back` },
  { threshold: 750, text: () => `That's like running Bengaluru to Hyderabad` },
  { threshold: 1000, text: () => `That's Chennai to Bengaluru and back` },
  { threshold: 1200, text: () => `That's like running Mumbai to Hyderabad` },
  { threshold: 1400, text: () => `That's Delhi to Mumbai distance` },
  { threshold: 1600, text: () => `That's like running Bengaluru to Delhi` },
  { threshold: 1800, text: () => `You've covered nearly the length of North India` },
  { threshold: 2000, text: () => `That's Delhi to Chennai distance` },
  { threshold: 2500, text: () => `That's like running Mumbai to Kolkata` },
  { threshold: 3000, text: () => `That's nearly Kashmir to Kanyakumari halfway` },
  { threshold: 3500, text: () => `You've almost run the full length of India 🇮🇳` },
  { threshold: 3700, text: () => `That's Kashmir to Kanyakumari distance` },
  { threshold: 4000, text: () => `You've run across India and kept going` },
  { threshold: 5000, text: () => `You've run across India… and halfway back` },
  { threshold: 6000, text: () => `That's like running India coast to coast twice` },
  { threshold: 7500, text: () => `You've covered ultra-legend territory` },
  { threshold: 10000, text: () => `You've run across India nearly three times 🇮🇳🔥` },
];

const ELEVATION_COMPARISONS = [
  { threshold: 1000, text: (e) => `${(e / 8849).toFixed(1)}\u00d7 Mount Everest in elevation` },
  { threshold: 5000, text: (e) => `You've climbed ${Math.floor(e / 8849)} Everests` },
];

/**
 * SharePage — public view of a runner's resume.
 * No authentication required. Reads from the `publicResumes` collection.
 */
export class SharePage {
  constructor() {
    /** @type {ResumePreview|null} */
    this._preview = null;
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Render the share page into the given container.
   * @param {HTMLElement} container
   */
  async render(container) {
    if (!container) return;

    // 1. Parse resumeId from URL
    const resumeId = this._parseResumeId();

    if (!resumeId) {
      this._render404(container);
      return;
    }

    // 2. Show loading skeleton
    this._renderLoading(container);

    try {
      // 3. Fetch from Firestore: publicResumes/{resumeId}
      const docRef = doc(db, 'publicResumes', resumeId);
      const snap = await getDoc(docRef);

      if (!snap.exists()) {
        this._render404(container);
        return;
      }

      // 5. Deserialize and render
      const data = snap.data();
      const resume = Resume.fromFirestore(snap.id, data);
      const stats = StatsCalculator.calculate(resume.races);
      const templateId = data.templateId || resume.templateId || 'classic';

      // 6. Set page title
      const safeName = (resume.name || 'Runner').replace(/[<>"'&]/g, '');
      document.title = `${safeName}'s Running Resume`;

      // Clear loading state
      container.innerHTML = '';

      // Build page structure
      const page = document.createElement('div');
      page.className = 'share-page';

      // -- Resume card via ResumePreview --
      const previewSection = document.createElement('section');
      previewSection.className = 'share-page__preview';

      this._preview = new ResumePreview();
      this._preview.render(previewSection);
      this._preview.update({ resume, stats, templateId });

      // Remove the duplicate stats row from inside the resume card
      const cardStats = previewSection.querySelector('.tpl-classic__stats');
      if (cardStats) cardStats.remove();

      page.appendChild(previewSection);

      // -- Animated stats (below the name section, above race list) --
      const statsSection = this._buildStatsSection(stats);
      page.appendChild(statsSection);

      // -- Fun comparisons --
      const comparisons = this._buildComparisons(stats);
      if (comparisons) {
        page.appendChild(comparisons);
      }

      // -- Copy link button --
      const copySection = this._buildCopySection();
      page.appendChild(copySection);

      // -- Footer --
      const footer = this._buildFooter();
      page.appendChild(footer);

      container.appendChild(page);
    } catch (err) {
      console.error('SharePage: failed to load resume', err);
      this._render404(container, 'Something went wrong. Please try again later.');
    }
  }

  // ---------------------------------------------------------------------------
  // Private — URL parsing
  // ---------------------------------------------------------------------------

  _parseResumeId() {
    // Try path: /r/{id}
    const pathMatch = window.location.pathname.match(/\/r\/([^/?#]+)/);
    if (pathMatch) return pathMatch[1];

    // Fallback: ?id={id}
    const params = new URLSearchParams(window.location.search);
    const queryId = params.get('id');
    if (queryId) return queryId;

    // Fallback: window.__RESUME_ID__ (set by the SSR proxy)
    if (typeof window !== 'undefined' && window.__RESUME_ID__) {
      return window.__RESUME_ID__;
    }

    return null;
  }

  // ---------------------------------------------------------------------------
  // Private — states
  // ---------------------------------------------------------------------------

  _renderLoading(container) {
    container.innerHTML = '';

    const wrapper = document.createElement('div');
    wrapper.className = 'share-page share-page--loading';

    wrapper.innerHTML = `
      <div class="share-skeleton">
        <div class="share-skeleton__header"></div>
        <div class="share-skeleton__identity">
          <div class="share-skeleton__avatar"></div>
          <div class="share-skeleton__name-block">
            <div class="share-skeleton__line share-skeleton__line--wide"></div>
            <div class="share-skeleton__line share-skeleton__line--medium"></div>
          </div>
        </div>
        <div class="share-skeleton__stats">
          <div class="share-skeleton__stat-card"></div>
          <div class="share-skeleton__stat-card"></div>
          <div class="share-skeleton__stat-card"></div>
        </div>
        <div class="share-skeleton__rows">
          <div class="share-skeleton__line share-skeleton__line--full"></div>
          <div class="share-skeleton__line share-skeleton__line--full"></div>
          <div class="share-skeleton__line share-skeleton__line--full"></div>
          <div class="share-skeleton__line share-skeleton__line--wide"></div>
        </div>
      </div>
    `;

    container.appendChild(wrapper);
  }

  _render404(container, message) {
    container.innerHTML = '';

    const wrapper = document.createElement('div');
    wrapper.className = 'share-page share-page--404';

    const msg = message || "This resume doesn't exist or has been removed.";

    wrapper.innerHTML = `
      <div class="share-404">
        <div class="share-404__icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <path d="M16 16s-1.5-2-4-2-4 2-4 2"/>
            <line x1="9" y1="9" x2="9.01" y2="9"/>
            <line x1="15" y1="9" x2="15.01" y2="9"/>
          </svg>
        </div>
        <h1 class="share-404__title">Resume Not Found</h1>
        <p class="share-404__message">${msg}</p>
        <a href="/" class="share-404__link">Go to Home</a>
      </div>
    `;

    container.appendChild(wrapper);
  }

  // ---------------------------------------------------------------------------
  // Private — build sections
  // ---------------------------------------------------------------------------

  _buildStatsSection(stats) {
    const section = document.createElement('section');
    section.className = 'share-stats';

    const grid = document.createElement('div');
    grid.className = 'share-stats__grid';

    // Total distance
    if (stats.totalDistance > 0) {
      grid.appendChild(
        AnimatedStat.create({
          value: Math.round(stats.totalDistance),
          label: 'Total Distance',
          suffix: 'km',
          duration: 1500,
          decimals: 0,
        })
      );
    }

    // Race count
    if (stats.raceCount > 0) {
      grid.appendChild(
        AnimatedStat.create({
          value: stats.raceCount,
          label: 'Races',
          suffix: '',
          duration: 1200,
          decimals: 0,
        })
      );
    }

    // Total elevation
    if (stats.totalElevation > 0) {
      grid.appendChild(
        AnimatedStat.create({
          value: Math.round(stats.totalElevation),
          label: 'Elevation Gained',
          suffix: 'm',
          duration: 1600,
          decimals: 0,
        })
      );
    }

    // Marathon PB
    if (stats.prByDistance && stats.prByDistance.has('Marathon')) {
      const pr = stats.prByDistance.get('Marathon');
      if (pr.time) {
        // Parse HH:MM:SS to minutes for animation
        const parts = pr.time.split(':').map(Number);
        const totalMin = parts[0] * 60 + parts[1];
        const display = `${parts[0]}:${String(parts[1]).padStart(2, '0')}`;
        grid.appendChild(
          AnimatedStat.create({
            value: totalMin,
            label: 'Marathon PB',
            suffix: '',
            duration: 1400,
            decimals: 0,
            formatValue: () => display,
          })
        );
      }
    }

    section.appendChild(grid);
    return section;
  }

  _buildComparisons(stats) {
    const lines = [];

    // Distance comparison — pick the highest threshold exceeded
    if (stats.totalDistance > 0) {
      let best = null;
      for (const c of DISTANCE_COMPARISONS) {
        if (stats.totalDistance >= c.threshold) {
          best = c;
        }
      }
      if (best) {
        lines.push(best.text(stats.totalDistance));
      }
    }

    // Elevation comparison
    if (stats.totalElevation > 0) {
      let best = null;
      for (const c of ELEVATION_COMPARISONS) {
        if (stats.totalElevation >= c.threshold) {
          best = c;
        }
      }
      if (best) {
        lines.push(best.text(stats.totalElevation));
      }
    }

    if (lines.length === 0) return null;

    const section = document.createElement('section');
    section.className = 'share-comparisons';

    lines.forEach((line) => {
      const p = document.createElement('p');
      p.className = 'share-comparisons__line';
      p.textContent = line;
      section.appendChild(p);
    });

    return section;
  }

  _buildCopySection() {
    const section = document.createElement('div');
    section.className = 'share-copy';

    const btn = document.createElement('button');
    btn.className = 'share-copy__btn';
    btn.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
      </svg>
      <span>Copy Link</span>
    `;

    btn.addEventListener('click', async () => {
      try {
        await writeTextToClipboard(window.location.href);
        Toast.show('Link copied to clipboard!', 'success');
      } catch (error) {
        console.error('Failed to copy share link:', error);
        Toast.show('Clipboard copy failed. Please copy the link manually.', 'error');
      }
    });

    section.appendChild(btn);
    return section;
  }

  _buildFooter() {
    const footer = document.createElement('footer');
    footer.className = 'share-footer';

    footer.innerHTML = `
      <span class="share-footer__text">Created using</span>
      <a href="https://therunninggenie.com/resume" class="share-footer__link">The Running Genie</a>
      <span class="share-footer__sep">&middot;</span>
      <a href="https://therunninggenie.com/resume" class="share-footer__cta">Create yours →</a>
    `;

    return footer;
  }
}
